import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Edit, Trash2 } from "lucide-react";
import { WeightedValueDisplay } from "./WeightedValueDisplay";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  statusDisplayMap,
  getStatusColor,
  getStatusIcon,
  getProposalStatus,
} from "@/constants/proposalMeta";
import { useNavigate } from "react-router-dom";

interface ProposalTableProps {
  proposals: any[];
  isLoading?: boolean;
  onView: (proposal: any) => void;
  onEdit: (proposal: any) => void;
  onDelete: (proposal: any) => void;
}

export function ProposalTable({ proposals, isLoading, onView, onEdit, onDelete }: ProposalTableProps) {
  const navigate = useNavigate();
  if (proposals.length === 0) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium">No proposals found</h3>
            <p className="text-muted-foreground">
              Try adjusting your filters or create a new proposal.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableBody>
              {proposals.map((proposal) => {
                const actualStatus = getProposalStatus(proposal);
                const StatusIcon = getStatusIcon(actualStatus);
                return (
                  <TableRow key={proposal.id}>
                    <TableCell colSpan={6} className="py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                          {/* Title row - clickable, no icon */}
                          <button
                            type="button"
                            onClick={() => onView(proposal)}
                            className="text-base md:text-lg font-semibold leading-snug hover:underline text-foreground text-left"
                          >
                            {proposal.title}
                          </button>
                           {/* Second row with client, value, probability and expected invoicing date - no icons */}
                           <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-3">
                             <Tooltip>
                               <TooltipTrigger asChild>
                                 <button
                                   className="hover:text-primary transition-colors hover:underline text-purple-600 dark:text-purple-400"
                                   onClick={() => navigate('/clients')}
                                 >
                                   {proposal.clients?.name}
                                 </button>
                               </TooltipTrigger>
                               <TooltipContent>
                                 <div className="text-sm">
                                   <p className="font-medium">{proposal.clients?.name}</p>
                                   {proposal.clients?.industry && (
                                     <p className="text-muted-foreground">Industry: {proposal.clients.industry}</p>
                                   )}
                                   <p className="text-xs text-muted-foreground mt-1">Click to view client details</p>
                                 </div>
                               </TooltipContent>
                             </Tooltip>
                              <span>•</span>
                              <div className="font-bold">
                                <WeightedValueDisplay 
                                  amount={proposal.final_value || proposal.value}
                                  probability={proposal.probability}
                                  showProbability={true}
                                  className="text-sm"
                                />
                              </div>
                              {proposal.expected_invoicing_date && (
                                <>
                                  <span>•</span>
                                  <span className="text-xs">
                                    Expected invoicing: {new Date(proposal.expected_invoicing_date + 'T00:00:00').toLocaleDateString('el-GR')}
                                  </span>
                                </>
                              )}
                              {proposal.decision_date && (proposal.status === 'accepted' || proposal.status === 'rejected') && (
                                <>
                                  <span>•</span>
                                  <span className="text-xs">
                                    Decision: {new Date(proposal.decision_date + 'T00:00:00').toLocaleDateString('el-GR')}  
                                  </span>
                                </>
                              )}
                           </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={getStatusColor(actualStatus)}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusDisplayMap[actualStatus as keyof typeof statusDisplayMap]}
                          </Badge>
                          <div className="flex items-center gap-2">
                            {/* Removed View button */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => onEdit(proposal)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => onDelete(proposal)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
